import * as React from 'react'

import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import CheckboxCheckedIcon from '../icons/CheckboxCheckedIcon'
import CheckboxUncheckedIcon from '../icons/CheckboxUncheckedIcon'
import { getColor } from '../theme/colors'
import LoadingIcon from './LoadingIcon'

export type CheckboxInputLabelProps = {
  checked?: boolean
  disabled?: boolean
  invalid?: boolean
  variant?: 'primary' | 'secondary'
  highlight?: boolean
}

export type CheckboxInputProps = {
  checkedByDefault?: boolean
  className?: string
  value?: string
  label: string | React.ReactNode
  loading?: boolean
  subtext?: string
  onChange?: React.InputHTMLAttributes<HTMLInputElement>['onChange']
  tooltip?: React.ReactNode
} & CheckboxInputLabelProps

const StyledLabel = styled.label<CheckboxInputLabelProps>`
  border-radius: 1rem;
  cursor: pointer;
  user-select: none;
  position: relative;

  input {
    appearance: none;
    opacity: 0;
    position: absolute;
    left: 0;
    top: 0;
    min-width: 1px;
    min-height: 1px;
  }

  & > span {
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    & > svg {
      flex-basis: 1.5rem;
      flex-shrink: 0;
    }
  }

  ${props => {
    switch (props.variant) {
      case 'secondary':
        return css`
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1rem;
          height: 100%;

          background-color: ${props.theme.colors.formFieldBackground};
          border: 2px solid ${props.theme.colors.formFieldBackground};

          ${props.checked &&
          css`
            background-color: ${props.theme.colors.background};
            border: 2px solid ${props.theme.colors.checkboxSelected};
          `}

          ${props.theme.typography.buttonText}
                    color: ${props.theme.colors.label};
        `
      case 'primary':
      default:
        return css`
          ${props.theme.typography.bodyText}
          color: ${props.theme.colors.secondaryLabel};
        `
    }
  }}

  ${props =>
    props.disabled &&
    css`
      color: ${props.theme.colors.disabledLabel};
    `}
  ${props =>
    props.invalid &&
    css`
      border-color: ${props.theme.colors.error};
    `}
    ${props =>
    props.highlight &&
    props.checked &&
    css`
      background-color: ${props.theme.colors.bannerBackgroundSuccess};
    `}
`
const CheckboxTextContainer = styled.div`
  width: 100%;
  ${props => props.theme.breakpoints.tablet} {
    width: auto;
  }
`

const CheckboxLabelContainer = styled.div`
  flex: 1;
  display: flex;
`

const TooltipContainer = styled.div`
  height: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-left: 0.625rem;
  margin-right: 0.5rem;
`

const CheckboxLabel = styled.span`
  margin-left: 1rem;
  flex: 1;
  display: flex;
  align-items: center;
`

const SubText = styled(CheckboxLabel)`
  color: ${getColor('secondaryLabel')};
  ${props => props.theme.typography.bodyText}
`

const Checkbox = ({
  checked,
  loading,
  disabled,
  variant,
}: Pick<CheckboxInputProps, 'checked' | 'loading' | 'disabled' | 'variant'>) => {
  const theme = useTheme()
  if (loading) {
    return <LoadingIcon color="checkboxSelected" size="1.5rem" />
  }

  return checked ? (
    <CheckboxCheckedIcon opacity={disabled ? 0.5 : 1} />
  ) : (
    <CheckboxUncheckedIcon
      opacity={disabled ? 0.5 : 1}
      color={variant === 'secondary' ? theme.colors.withinGrayCardInputOutline : theme.colors.inputOutline}
    />
  )
}

const CheckboxInput = React.forwardRef<HTMLInputElement, CheckboxInputProps>(
  (
    { checked, value, disabled, loading, invalid, label, subtext, variant, tooltip, highlight, className, ...props },
    ref
  ) => {
    return (
      <StyledLabel
        {...{ checked, disabled, invalid, variant, highlight }}
        role="checkbox"
        aria-checked={checked}
        className={className}
      >
        <span>
          <Checkbox checked={checked} loading={loading} disabled={disabled} variant={variant} />
          <CheckboxTextContainer>
            <CheckboxLabelContainer>
              <CheckboxLabel>{label}</CheckboxLabel>
              {tooltip && <TooltipContainer>{tooltip}</TooltipContainer>}
            </CheckboxLabelContainer>
            <SubText>{subtext}</SubText>
          </CheckboxTextContainer>
        </span>
        <input type="checkbox" {...props} {...{ value, checked, disabled }} ref={ref} />
      </StyledLabel>
    )
  }
)

export default CheckboxInput
